import { Logo } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - ezRecipeBox</title>
        <meta name="description" content="Terms of Service for ezRecipeBox - Read our terms and conditions for using the ezRecipeBox service." />
      </Helmet>
      <div className="flex flex-col min-h-screen">
        <header className="px-4 lg:px-6 h-16 flex items-center shadow-sm">
          <Link to="/" className="flex items-center justify-center">
            <Logo className="h-6 w-6 text-primary" />
            <span className="sr-only">ezRecipeBox</span>
          </Link>
          <nav className="ml-auto flex gap-4 sm:gap-6">
            <Button variant="ghost" asChild>
              <Link to="/login">
                Login
              </Link>
            </Button>
            <Button asChild>
              <Link to="/signup">
                Sign Up
              </Link>
            </Button>
          </nav>
        </header>
        <main className="flex-1 container mx-auto px-4 py-8 md:py-12 max-w-4xl">
          <article className="prose prose-slate max-w-none dark:prose-invert">
            <h1 className="font-headline text-4xl font-bold mb-4">Terms of Service</h1>
            <p className="text-muted-foreground mb-6">
              <strong>Last Updated:</strong> January 2025
            </p>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing or using ezRecipeBox (&quot;Service&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you disagree with any part of these terms, you may not access the Service. These Terms apply to all visitors, users, and others who access or use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">2. Description of Service</h2>
              <p>
                ezRecipeBox is a recipe management and sharing platform that allows users to create, organize, store, and share recipes with family and friends. The Service includes features such as recipe boxes, recipe sharing, image and video uploads, and collaborative editing.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">3. User Accounts</h2>
              <h3 className="font-semibold text-xl mt-6 mb-3">3.1 Account Creation</h3>
              <p>
                To use certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
              </p>

              <h3 className="font-semibold text-xl mt-6 mb-3">3.2 Account Security</h3>
              <p>
                You are responsible for safeguarding your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
              </p>

              <h3 className="font-semibold text-xl mt-6 mb-3">3.3 Account Termination</h3>
              <p>
                You may terminate your account at any time. We reserve the right to suspend or terminate your account if you violate these Terms or engage in any fraudulent, abusive, or illegal activity.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">4. User Content</h2>
              <h3 className="font-semibold text-xl mt-6 mb-3">4.1 Content Ownership</h3>
              <p>
                You retain all ownership rights to the recipes, images, videos, and other content you create or upload to the Service (&quot;User Content&quot;). By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, distribute, and display your User Content solely for the purpose of providing and improving the Service.
              </p>

              <h3 className="font-semibold text-xl mt-6 mb-3">4.2 Content Sharing</h3>
              <p>
                When you share recipes in a recipe box, other members of that recipe box can view, edit (if permitted), and use your shared recipes. You are responsible for ensuring you have the right to share any content you post.
              </p>

              <h3 className="font-semibold text-xl mt-6 mb-3">4.3 Prohibited Content</h3>
              <p>You agree not to post, upload, or transmit any User Content that:</p>
              <ul>
                <li>Violates any applicable law or regulation</li>
                <li>Infringes on the rights of others, including intellectual property rights</li>
                <li>Is defamatory, obscene, pornographic, or offensive</li>
                <li>Contains viruses, malware, or other harmful code</li>
                <li>Spams or solicits other users</li>
                <li>Impersonates another person or entity</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">5. Intellectual Property Rights</h2>
              <p>
                The Service and its original content, features, and functionality are owned by ezRecipeBox and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. Our trademarks and trade dress may not be used without our prior written consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">6. Recipe Scraping and Third-Party Content</h2>
              <p>
                Our Service may include features that allow you to import recipes from third-party websites. You are responsible for ensuring you have the right to import and use such content. We do not claim ownership of recipes scraped from third-party sites, and we are not responsible for the accuracy or legality of such content.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">7. Subscription and Payment Terms</h2>
              <p>
                Some features of the Service may be available on a subscription basis. By subscribing, you agree to pay the fees specified at the time of purchase. Subscriptions automatically renew unless cancelled. You may cancel your subscription at any time, and cancellation will take effect at the end of the current billing period.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">8. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul>
                <li>Use the Service for any illegal purpose or in violation of any applicable laws</li>
                <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                <li>Attempt to gain unauthorized access to any portion of the Service</li>
                <li>Use automated systems (bots, scrapers) to access the Service without permission</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Use the Service to transmit any malicious code or harmful content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">9. Disclaimers</h2>
              <p>
                THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">10. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ezRecipebox SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">11. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless ezRecipeBox, its officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including attorney&apos;s fees) arising from your use of the Service, your User Content, or your violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">12. Changes to Terms</h2>
              <p>
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. Your continued use of the Service after any changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">13. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in [Your Jurisdiction].
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">14. Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect and enforceable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">15. Contact Information</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <p className="mt-4">
                <strong>Email:</strong> [Your Support Email Address]<br />
                <strong>Website:</strong> https://[your-domain].com
              </p>
            </section>
          </article>
        </main>
        <footer className="flex flex-col gap-2 sm:flex-row py-6 w-full shrink-0 items-center px-4 md:px-6 border-t justify-between">
          <p className="text-xs text-muted-foreground">&copy; 2025 ezRecipeBox. All rights reserved.</p>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground underline">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-foreground underline">
              Terms of Service
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
}

